import { GridColDef } from "@mui/x-data-grid-pro";
import {
  ArrowRightIcon,
  GKButton,
  GKCheckbox,
  GKClientDataGrid,
  GKDatePicker,
  GKSearchSelect,
  Grid2,
  Show,
  Typography,
} from "components";
import dayjs from "dayjs";
import DashboardLayout from "layouts/DashboardLayout";
import { useState } from "react";
import { Controller, useFieldArray, useForm } from "react-hook-form";
import { useLocation, useNavigate } from "react-router-dom";
import {
  useBulkAutomationMutation,
  useLazyGetPortfolioByFeePlanQuery,
} from "store/features/Revenue/ProformaApi";
import { useAppSelector } from "store/store";
import { extraColumnsField } from "utils/helpers";
import useDocumentTitle from "utils/useDocumentTitle";

interface AutomationRow {
  portfolio?: string | number;
  startingDate?: string;
  isFeeCalculation?: boolean;
  isProforma?: boolean;
  isPaymentCollection?: boolean;
  id?: number | null;
  clientName?: string;
  portfolioName?: string;
  portfolioId?: string | number;
}

interface BulkAutomationFormData {
  feePlan: string | number;
  autoInvoiceAndFlowHistory: AutomationRow[];
}

const BulkAutomationForm = () => {
  const navigate = useNavigate();
  const location = useLocation();

  useDocumentTitle(
    `${location.pathname?.includes("update") ? "Update" : "Add"} Bulk Automation`,
  );

  const feePlanList = useAppSelector((state) => state?.app?.feePlanList);

  const form = useForm<BulkAutomationFormData>({
    defaultValues: { feePlan: "", autoInvoiceAndFlowHistory: [] },
    mode: "onSubmit",
  });

  const { fields, replace } = useFieldArray({
    name: "autoInvoiceAndFlowHistory",
    control: form.control,
  });

  const [selectedRows, setSelectedRows] = useState<number[]>([]);

  const [triggerGetPortfolios, { data, isFetching, isSuccess }] =
    useLazyGetPortfolioByFeePlanQuery();

  const [bulkAutomation, { isLoading }] = useBulkAutomationMutation();

  const watchedRows = form.watch("autoInvoiceAndFlowHistory");

  const columns: GridColDef[] = [
    {
      ...extraColumnsField(
        `Portfolio Name (${data?.totalCount || 0})`,
        "clientName",
        250,
        1,
      ),
      renderCell: (params) => {
        return (
          <Grid2 container flexDirection="column" alignItems="flex-start">
            <Typography fontWeight={700} noWrap>
              {params.row.clientName}
            </Typography>
            <Typography fontSize={12}>
              {params.row.portfolioName} - {params.row.portfolioId}
            </Typography>
          </Grid2>
        );
      },
    },
    {
      ...extraColumnsField("Billing Date", "startingDate", 120, 1),
      renderCell: (params) => {
        const { rowIndex } = params.row;
        return (
          <Controller
            control={form.control}
            name={`autoInvoiceAndFlowHistory.${rowIndex}.startingDate`}
            rules={{ required: selectedRows.includes(rowIndex) }}
            render={({ field, fieldState: { error } }) => (
              <GKDatePicker
                {...field}
                disabled={
                  !selectedRows.includes(rowIndex) ||
                  location.pathname?.includes("update")
                }
                value={field.value ? dayjs(field.value) : null}
                onChange={(value) =>
                  field.onChange(value ? dayjs(value).format("YYYY-MM-DD") : "")
                }
                disableFuture
                slotProps={{
                  textField: { size: "small", error: !!error },
                }}
              />
            )}
          />
        );
      },
    },
    {
      ...extraColumnsField("Fee Calculation", "isFeeCalculation", 120, 1),
      sortable: false,
      renderHeader: () => {
        const enabledSelectedIndexes = selectedRows;

        const allChecked =
          enabledSelectedIndexes.length > 0 &&
          enabledSelectedIndexes.every(
            (idx) => !!watchedRows?.[idx]?.isFeeCalculation,
          );

        const someChecked =
          enabledSelectedIndexes.some(
            (idx) => !!watchedRows?.[idx]?.isFeeCalculation,
          ) && !allChecked;

        return (
          <Grid2 container gap={2} display={"flex"} alignItems={"center"}>
            <GKCheckbox
              checked={allChecked}
              indeterminate={someChecked}
              onChange={(e) => {
                const { checked } = e.target;
                enabledSelectedIndexes.forEach((idx) => {
                  form.setValue(
                    `autoInvoiceAndFlowHistory.${idx}.isFeeCalculation`,
                    checked,
                  );
                });
              }}
            />
            <Typography fontWeight={600} fontSize={12} color={"var(--black)"}>
              Fee Calculation
            </Typography>
          </Grid2>
        );
      },
      renderCell: (params) => {
        const { rowIndex } = params.row;

        return (
          <Controller
            control={form.control}
            name={`autoInvoiceAndFlowHistory.${rowIndex}.isFeeCalculation`}
            render={({ field }) => (
              <GKCheckbox
                disableLeftPadding
                disabled={!selectedRows.includes(rowIndex)}
                checked={!!field.value}
                onChange={(e) => field.onChange(e.target.checked)}
              />
            )}
          />
        );
      },
    },
    {
      ...extraColumnsField("Proforma Creation", "isProforma", 120, 1),
      sortable: false,
      renderHeader: () => {
        const enabledSelectedIndexes = selectedRows;

        const allChecked =
          enabledSelectedIndexes.length > 0 &&
          enabledSelectedIndexes.every(
            (idx) => !!watchedRows?.[idx]?.isProforma,
          );

        const someChecked =
          enabledSelectedIndexes.some(
            (idx) => !!watchedRows?.[idx]?.isProforma,
          ) && !allChecked;

        return (
          <Grid2 container gap={2} display={"flex"} alignItems={"center"}>
            <GKCheckbox
              checked={allChecked}
              indeterminate={someChecked}
              onChange={(e) => {
                const { checked } = e.target;
                enabledSelectedIndexes.forEach((idx) => {
                  form.setValue(
                    `autoInvoiceAndFlowHistory.${idx}.isProforma`,
                    checked,
                  );
                  form.setValue(
                    `autoInvoiceAndFlowHistory.${idx}.isFeeCalculation`,
                    e.target.checked,
                  );
                });
              }}
            />
            <Typography fontWeight={600} fontSize={12} color={"var(--black)"}>
              Proforma Creation
            </Typography>
          </Grid2>
        );
      },
      renderCell: (params) => {
        const { rowIndex } = params.row;
        return (
          <Controller
            control={form.control}
            name={`autoInvoiceAndFlowHistory.${rowIndex}.isProforma`}
            render={({ field }) => (
              <GKCheckbox
                disableLeftPadding
                disabled={!selectedRows.includes(rowIndex)}
                checked={!!field.value}
                onChange={(e) => {
                  form.setValue(
                    `autoInvoiceAndFlowHistory.${rowIndex}.isFeeCalculation`,
                    e.target.checked,
                  );
                  field.onChange(e.target.checked);
                }}
              />
            )}
          />
        );
      },
    },
    {
      ...extraColumnsField("Payment", "isPaymentCollection", 120, 1),
      sortable: false,
      renderHeader: () => {
        const enabledSelectedIndexes = selectedRows;

        const allChecked =
          enabledSelectedIndexes.length > 0 &&
          enabledSelectedIndexes.every(
            (idx) => !!watchedRows?.[idx]?.isPaymentCollection,
          );

        const someChecked =
          enabledSelectedIndexes.some(
            (idx) => !!watchedRows?.[idx]?.isPaymentCollection,
          ) && !allChecked;

        return (
          <Grid2 container gap={2} display={"flex"} alignItems={"center"}>
            <GKCheckbox
              checked={allChecked}
              indeterminate={someChecked}
              onChange={(e) => {
                const { checked } = e.target;
                enabledSelectedIndexes.forEach((idx) => {
                  form.setValue(
                    `autoInvoiceAndFlowHistory.${idx}.isPaymentCollection`,
                    checked,
                  );
                  form.setValue(
                    `autoInvoiceAndFlowHistory.${idx}.isFeeCalculation`,
                    e.target.checked,
                  );
                  form.setValue(
                    `autoInvoiceAndFlowHistory.${idx}.isProforma`,
                    e.target.checked,
                  );
                });
              }}
            />
            <Typography fontWeight={600} fontSize={12} color={"var(--black)"}>
              Payment
            </Typography>
          </Grid2>
        );
      },
      renderCell: (params) => {
        const { rowIndex } = params.row;
        return (
          <Controller
            control={form.control}
            name={`autoInvoiceAndFlowHistory.${rowIndex}.isPaymentCollection`}
            render={({ field }) => (
              <GKCheckbox
                disableLeftPadding
                disabled={!selectedRows.includes(rowIndex)}
                checked={!!field.value}
                onChange={(e) => {
                  form.setValue(
                    `autoInvoiceAndFlowHistory.${rowIndex}.isFeeCalculation`,
                    e.target.checked,
                  );
                  form.setValue(
                    `autoInvoiceAndFlowHistory.${rowIndex}.isProforma`,
                    e.target.checked,
                  );
                  field.onChange(e.target.checked);
                }}
              />
            )}
          />
        );
      },
    },
  ];

  const handleSubmit = async (data: BulkAutomationFormData) => {
    try {
      const filtered = selectedRows.map(
        (i) => data.autoInvoiceAndFlowHistory[i],
      );
      const payload: BulkAutomationFormData = {
        feePlan: data.feePlan,
        autoInvoiceAndFlowHistory: filtered,
      };

      await bulkAutomation({
        isUpdate: location.pathname?.includes("update"),
        data: payload,
      }).unwrap();

      form.reset();
      setSelectedRows([]);
      navigate(-1);
    } catch (err) {
      console.error("Bulk automation failed:", err);
    }
  };

  const handleGetPortfolioByFeePlan = async () => {
    try {
      const response = await triggerGetPortfolios({
        isUpdate: location.pathname?.includes("update"),
        id: form.getValues("feePlan"),
      }).unwrap();
      const data = response?.data || [];

      const normalized: AutomationRow[] = (data as any[]).map((r, idx) => ({
        id: r.id ?? idx,
        clientName: r.clientName ?? r.client_name ?? "",
        portfolioName: r.portfolioName ?? r.portfolio_name ?? "",
        portfolioId: r.portfolioId ?? r.portfolio_id ?? "",
        startingDate: r.startingDate ?? r.billing_date ?? "",
        isFeeCalculation: !!r.isFeeCalculation,
        isProforma: !!r.isProforma,
        isPaymentCollection: !!r.isPaymentCollection,
        portfolio: location.pathname?.includes("update") ? r?.portfolio : r.id,
      }));

      replace(normalized);
      setSelectedRows(
        location.pathname?.includes("update")
          ? normalized.map((_, index) => index)
          : [],
      );
    } catch (err) {
      console.error("Bulk automation failed:", err);
    }
  };

  return (
    <DashboardLayout title="Add Bulk Automation">
      <form onSubmit={form.handleSubmit(handleSubmit)}>
        <Grid2 container spacing={2}>
          <Grid2
            container
            size={12}
            p={2}
            borderRadius={2}
            bgcolor="var(--grey-bg-100)"
            spacing={2}
          >
            <Grid2 size={4}>
              <Controller
                control={form.control}
                name="feePlan"
                rules={{ required: "Please select fee plan" }}
                render={({ field, fieldState: { error } }) => (
                  <GKSearchSelect
                    {...field}
                    disableClearable
                    requiredField
                    inputLabel="Fee Plan"
                    options={feePlanList}
                    onChange={(event, val) => field.onChange(val?.value || "")}
                    value={
                      feePlanList.find((opt) => opt.value === field.value) ||
                      null
                    }
                    getOptionLabel={(option) => option.name || ""}
                    error={!!error}
                    helperText={error?.message}
                  />
                )}
              />
            </Grid2>

            <Grid2 size={2} display="flex" alignItems="center" mt="6px">
              <GKButton
                variant="contained"
                endIcon={<ArrowRightIcon />}
                sx={{ p: "16px 12px" }}
                loading={isFetching}
                onClick={handleGetPortfolioByFeePlan}
              >
                Submit
              </GKButton>
            </Grid2>

            <Grid2
              size={6}
              display="flex"
              justifyContent="flex-end"
              alignItems="flex-end"
            >
              <Typography
                fontSize={12}
                fontWeight={500}
                color="var(--neutral-500)"
              >
                Selected Portfolios:{" "}
                <Typography component="span" fontSize={12} fontWeight={600}>
                  {selectedRows.length}
                </Typography>
              </Typography>
            </Grid2>
          </Grid2>

          <Show.When isTrue={isSuccess && fields.length > 0}>
            <Grid2 size={12}>
              <GKClientDataGrid
                columns={columns}
                rows={fields.map((row, index) => ({
                  ...row,
                  rowIndex: index,
                  id: index,
                }))}
                showHeaderGrid={false}
                checkboxSelection
                disableRowSelectionOnClick
                rowSelectionModel={selectedRows}
                onRowSelectionModelChange={(newSelection) => {
                  const deselected = selectedRows.filter(
                    (idx) => !newSelection.includes(idx),
                  );
                  if (deselected.length) {
                    deselected.forEach((idx) => {
                      form.setValue(
                        `autoInvoiceAndFlowHistory.${idx}.startingDate`,
                        "",
                      );
                      form.setValue(
                        `autoInvoiceAndFlowHistory.${idx}.isFeeCalculation`,
                        false,
                      );
                      form.setValue(
                        `autoInvoiceAndFlowHistory.${idx}.isProforma`,
                        false,
                      );
                      form.setValue(
                        `autoInvoiceAndFlowHistory.${idx}.isPaymentCollection`,
                        false,
                      );
                    });
                  }
                  setSelectedRows(newSelection as number[]);
                }}
                name="automation"
              />
            </Grid2>

            <Grid2 size={12} display="flex" justifyContent="flex-end">
              <GKButton variant="contained" type="submit" loading={isLoading}>
                {location.pathname?.includes("update") ? "Update" : "Create"}{" "}
                Bulk Automation
              </GKButton>
            </Grid2>
          </Show.When>
        </Grid2>
      </form>
    </DashboardLayout>
  );
};

export default BulkAutomationForm;
